// Phase 999.4 Plan 07 — Step C: Hardware rows with debounced cross-org collision check (R7, D-04).
//
// SPEC R7 acceptance: typing a Name that already exists anywhere in the codebase
// surfaces a row-level collision message identifying the owning OrgLocation; the
// wizard's Next gate refuses to advance while any row carries a collisionError.
// CONTEXT D-04 sets the debounce knob at ~400ms — long enough that fast typing
// doesn't spam the endpoint, short enough that an operator who pauses to read
// the field gets the answer before they look up.
//
// Implementation notes:
//   - The effect is keyed off namesKey (a join of all hardware names) rather than
//     state.hardware itself — referencing the array literal in the dep list would
//     re-run the effect every render even when names are unchanged.
//   - setTimeout per row, clearTimeout in the effect cleanup. If a name is empty
//     we skip scheduling (no point asking the server about ''); the skipped slot
//     gets `null` in the handles array so the cleanup loop tolerates it.
//   - check-name failures are SOFT — Plan 04's transactional INSERT is the real
//     uniqueness gate; the Step C check is a cooperation gate per the threat
//     model (T-999.4-25). We simply skip surfacing an error when the network
//     drops; the operator gets the verdict at Confirm time instead.

import React, { useEffect } from 'react';
import { Form, Input, Button, Table, FormText } from 'reactstrap';
import { apiBase } from '../apiBase';

const DEBOUNCE_MS = 400; // D-04 ~400ms

export function StepC_Hardware({ state, dispatch, auth }) {
    // Dependency-key hack: useEffect deps must be value-equal to skip re-runs;
    // an array literal of name strings would always be reference-different. The
    // joined string is value-equal across renders that didn't touch any name.
    const namesKey = state.hardware.map((h) => h.name).join('|');

    useEffect(() => {
        const handles = state.hardware.map((row, idx) => {
            if (!row.name) return null; // empty — no probe; UI shows blank field, Next is disabled by canProceed
            const handle = setTimeout(async () => {
                try {
                    let token = '';
                    if (auth && typeof auth.getAccessToken === 'function') {
                        try { token = auth.getAccessToken(); } catch { /* server gate handles */ }
                    }
                    const res = await fetch(
                        `${apiBase()}/api/admin/hardware/check-name?name=${encodeURIComponent(row.name)}`,
                        { headers: token ? { Authorization: 'Bearer ' + token } : {} },
                    );
                    if (!res.ok) return; // soft-fail — Plan 04 POST is the authoritative uniqueness gate
                    const body = await res.json();
                    if (body && body.taken) {
                        dispatch({
                            type: 'UPDATE_HARDWARE',
                            idx,
                            patch: {
                                collisionError:
                                    `${row.name} is already assigned to LocationId ${body.locationId} ` +
                                    `(${body.locationName} / ${body.orgName}). Pick a different name.`,
                            },
                        });
                    } else {
                        dispatch({ type: 'UPDATE_HARDWARE', idx, patch: { collisionError: null } });
                    }
                } catch {
                    // soft-fail on network error — see comment above
                }
            }, DEBOUNCE_MS);
            return handle;
        });
        return () => { handles.forEach((h) => h && clearTimeout(h)); };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- state.hardware referenced via namesKey
    }, [namesKey, auth, dispatch]);

    return (
        <Form>
            <h4>Step C — Hardware</h4>
            <FormText>
                Add one row per Dipstick. Names must be globally unique (collision-checked across all orgs).
            </FormText>
            <Table size="sm" bordered className="mt-2">
                <thead>
                    <tr><th>Name</th><th>Description</th><th /></tr>
                </thead>
                <tbody>
                    {state.hardware.map((row, idx) => (
                        <tr key={idx} data-testid={`step-c-row-${idx}`}>
                            <td>
                                <Input
                                    type="text"
                                    value={row.name}
                                    onChange={(e) => dispatch({
                                        type: 'UPDATE_HARDWARE',
                                        idx,
                                        // Clear collisionError on edit — the debounced probe will re-set it if still taken.
                                        patch: { name: e.target.value, collisionError: null },
                                    })}
                                    invalid={!!row.collisionError}
                                    data-testid={`step-c-name-${idx}`}
                                />
                                {row.collisionError && (
                                    <div className="text-danger small" data-testid={`step-c-collision-${idx}`}>
                                        {row.collisionError}
                                    </div>
                                )}
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={row.description}
                                    onChange={(e) => dispatch({
                                        type: 'UPDATE_HARDWARE',
                                        idx,
                                        patch: { description: e.target.value },
                                    })}
                                    data-testid={`step-c-desc-${idx}`}
                                />
                            </td>
                            <td>
                                <Button
                                    color="link"
                                    size="sm"
                                    onClick={() => dispatch({ type: 'REMOVE_HARDWARE', idx })}
                                    data-testid={`step-c-remove-${idx}`}
                                >
                                    Remove
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Button
                color="secondary"
                size="sm"
                onClick={() => dispatch({ type: 'ADD_HARDWARE' })}
                data-testid="step-c-add-row"
            >
                + Add Hardware
            </Button>
        </Form>
    );
}

export default StepC_Hardware;
